import type { GridRowIdGetter } from '@mui/x-data-grid-pro';
import type { FC } from 'react';
import { INVOICE_ASSETS_EXPORT, NLA_EXPORT } from '../../constants/logic';
import {
	ASSETS,
	CONTRACTS,
	INVOICES,
	UPDATE_HISTORY,
	USER_ACCOUNTS,
	USERS,
} from '../../constants/messages';
import {
	assetViewOptions,
	invoiceAssetViewOptions,
	invoiceViewOptions,
	type ViewOptions,
} from '../../constants/viewOptions';
import { setSelectedAssetSerialNo } from '../../redux/slices/assetSlice';
import { setSelectedContractDetailsViewId } from '../../redux/slices/contractSlice';
import { setSelectedInvoiceDetailsViewId } from '../../redux/slices/invoiceSlice';
import {
	selectAssetView,
	selectInvoiceView,
	setAssetView,
	setInvoiceView,
} from '../../redux/slices/uiSettingsSlice';
import type { RootState } from '../../redux/store';
import {
	assetRequestTypes,
	contractRequestTypes,
	invoiceSupportRequestType,
	type RequestType,
} from '../Modals/RequestModal/RequestTypesDefinition';
import {
	mobileAssetColumns,
	mobileContractColumns,
	mobileInvoiceColumns,
} from './columnDefinitions';
import type { DataGridMoreActionKeys } from './DataGridMoreActions';

export type ExportType =
	| typeof CONTRACTS
	| typeof ASSETS
	| typeof INVOICES
	| typeof NLA_EXPORT
	| typeof INVOICE_ASSETS_EXPORT
	| typeof USERS;

export interface DataGridType {
	title: string;
	exportType?: ExportType;
	showTableViewSelector: boolean;
	showCheckBoxOnMobile: boolean;
	requestTypes?: RequestType[];
	tableViewOptions?: ViewOptions[];
	mobileColumns?: any[];
	selectedView?: (state: RootState) => any;
	setViewAction?: (payload: string) => any;
	setSelectedRecordsAction?: (payload: string) => any;
	moreActions?: DataGridMoreActionKeys[];
	printable?: boolean;
	searchBox?: FC;
	getRowId?: GridRowIdGetter;
}

export const ContractsDataGridType: DataGridType = {
	title: CONTRACTS,
	exportType: CONTRACTS,
	showTableViewSelector: false,
	showCheckBoxOnMobile: true,
	requestTypes: contractRequestTypes,
	setSelectedRecordsAction: setSelectedContractDetailsViewId,
	mobileColumns: mobileContractColumns,
	moreActions: [],
	printable: true,
};

export const AssetsDataGridType: DataGridType = {
	title: ASSETS,
	exportType: ASSETS,
	showTableViewSelector: true,
	showCheckBoxOnMobile: true,
	requestTypes: assetRequestTypes,
	tableViewOptions: assetViewOptions,
	selectedView: selectAssetView,
	setViewAction: setAssetView,
	setSelectedRecordsAction: setSelectedAssetSerialNo,
	mobileColumns: mobileAssetColumns,
	moreActions: ['editLeasedUserDefinedFields', 'uploadLeasedFile'],
	printable: true,
};

export const NonLeasedAssetsDataGridType: DataGridType = {
	title: ASSETS,
	exportType: NLA_EXPORT,
	showTableViewSelector: true,
	showCheckBoxOnMobile: true,
	requestTypes: assetRequestTypes,
	tableViewOptions: assetViewOptions,
	selectedView: selectAssetView,
	setViewAction: setAssetView,
	setSelectedRecordsAction: setSelectedAssetSerialNo,
	mobileColumns: mobileAssetColumns,
	moreActions: [
		'exportTemplate',
		'uploadFile',
		'editNonLeasedUserDefinedFields',
		'deleteNonLeasedAssets',
	],
	printable: true,
};

export const InvoicesDataGridType: DataGridType = {
	title: INVOICES,
	exportType: INVOICES,
	showTableViewSelector: true,
	showCheckBoxOnMobile: true,
	tableViewOptions: invoiceViewOptions,
	requestTypes: invoiceSupportRequestType,
	selectedView: selectInvoiceView,
	setViewAction: setInvoiceView,
	setSelectedRecordsAction: setSelectedInvoiceDetailsViewId,
	mobileColumns: mobileInvoiceColumns,
	moreActions: [],
	printable: true,
};

export const InvoiceAssetsDataGridType: DataGridType = {
	title: ASSETS,
	exportType: INVOICE_ASSETS_EXPORT,
	showTableViewSelector: true,
	showCheckBoxOnMobile: true,
	requestTypes: assetRequestTypes,
	tableViewOptions: invoiceAssetViewOptions,
	selectedView: selectAssetView,
	setViewAction: setAssetView,
	setSelectedRecordsAction: setSelectedAssetSerialNo,
	mobileColumns: mobileAssetColumns,
	moreActions: [],
	printable: true,
};

export const InvoiceRelatedAssetsDataGridType: DataGridType = {
	title: ASSETS,
	exportType: ASSETS,
	showTableViewSelector: false,
	showCheckBoxOnMobile: false,
	requestTypes: assetRequestTypes,
	tableViewOptions: assetViewOptions,
	selectedView: selectAssetView,
	setViewAction: setAssetView,
	mobileColumns: mobileAssetColumns,
	moreActions: [],
	printable: true,
};

export const UpdateHistoryDataGridType: DataGridType = {
	title: UPDATE_HISTORY,
	showTableViewSelector: false,
	showCheckBoxOnMobile: false,
	moreActions: [],
	printable: true,
	getRowId: (row: any) => row.historyId,
};

export const UserAccountsDataGridType: DataGridType = {
	title: USER_ACCOUNTS,
	exportType: USERS,
	showCheckBoxOnMobile: false,
	showTableViewSelector: false,
};
